import ArchiveWorkOrderIcon from "@shared/svg/archive-work-order.svg?react";
import ChevronDown from "@shared/svg/chevron-down.svg?react";
import DeleteIcon from "@shared/svg/delete.svg?react";
import EyeOpenIcon from "@shared/svg/eye-open.svg?react";
import MoreIcon from "@shared/svg/more.svg?react";
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuProvider,
  DropdownMenuTrigger,
} from "@shared/ui/DropdownMenu";
import { MakulaIcon } from "@shared/ui/MakulaIcon";
import { WorkOrderCard } from "@shared/ui/WorkOrderCard";
import moment from "moment-timezone";
import React from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { getWorkOrdersScrollScope } from "~/constants/scrollRestorationRoutes";
import useScrollPersist from "~/hooks/_useScrollPersist";
import { useChatStore } from "~/stores/chatStore";

interface Assignee {
  _id: string;
  name: string;
}

interface TicketType {
  icon?: string;
  color?: string;
  isInternal?: boolean;
}

interface Status {
  _id: string;
  label?: string;
  color?: string;
}

interface ScheduleDate {
  startTime?: string | number | Date;
}

interface WorkOrderListCardProps {
  id: string;
  path?: string;
  title?: string;
  assets?: { _id: string; name: string; serialNumber: string }[];
  ticketID?: string;
  ticketType?: TicketType;
  facility?: string;
  machine?: string;
  machineSerialNumber?: string;
  assignees?: Assignee[];
  status?: string;
  allStatuses?: Status[];
  ticketChannel?: string;
  ticketInternalChannel?: string;
  dueDate?: string;
  scheduleDate?: ScheduleDate | string;
  showArchive?: boolean;
  onArchive?: () => void;
  showUnarchive?: boolean;
  onUnarchive?: () => void;
  showDelete?: boolean;
  onDelete?: () => void;
  fromArchivedList?: boolean;
  showExpandIcon?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  subWorkOrderCount?: number;
  listQueryKey?: string;
  listItemsLoaded?: number;
  isSelectMode?: boolean;
  isSelected?: boolean;
  /** Force the checkbox to stay visible (set once ≥1 item is selected). */
  anySelected?: boolean;
  onToggleSelect?: () => void;
}

const computeDaysRemaining = (dueDate?: string): number | undefined => {
  if (!dueDate) return undefined;
  return moment(dueDate).diff(moment(), "days") + 1;
};

const WorkOrderListCard: React.FC<WorkOrderListCardProps> = ({
  id,
  path = "work/work-orders",
  title = "",
  ticketID = "",
  ticketType,
  facility,
  assets,
  assignees = [],
  status,
  allStatuses = [],
  ticketChannel,
  ticketInternalChannel,
  dueDate,
  scheduleDate,
  showArchive = false,
  onArchive,
  showUnarchive = false,
  onUnarchive,
  showDelete = false,
  onDelete,
  fromArchivedList = false,
  showExpandIcon = false,
  isExpanded = false,
  onToggleExpand,
  subWorkOrderCount = 0,
  listQueryKey,
  listItemsLoaded,
  isSelectMode = false,
  isSelected = false,
  anySelected = false,
  onToggleSelect,
}) => {
  const navigate = useNavigate();
  const { captureMainScroll } = useScrollPersist();
  const { messages } = useIntl() as { messages: Record<string, any> };
  const unreadChannelMessages = useChatStore(
    (s) => (s as any).unreadChannelMessages,
  );

  const notificationCount =
    (unreadChannelMessages?.channels?.[ticketChannel ?? ""] || 0) +
    (unreadChannelMessages?.channels?.[ticketInternalChannel ?? ""] || 0);

  const statusItem = allStatuses?.find((s) => s._id === status);
  const isClosedTicket = status === allStatuses?.[allStatuses.length - 1]?._id;

  const scheduledDateStr = (() => {
    if (!scheduleDate) return undefined;
    if (typeof scheduleDate === "string") return scheduleDate;
    if (scheduleDate.startTime)
      return new Date(scheduleDate.startTime).toISOString();
    return undefined;
  })();

  const daysRemaining = isClosedTicket
    ? undefined
    : computeDaysRemaining(dueDate);

  const handleNavigate = () => {
    captureMainScroll(getWorkOrdersScrollScope(), {
      ...(typeof listItemsLoaded === "number" ? { listItemsLoaded } : {}),
      ...(listQueryKey ? { listQueryKey } : {}),
    });
    navigate(`/app/${path}/${id}`, {
      state: fromArchivedList ? { fromArchived: true } : undefined,
    });
  };

  const expandButton = showExpandIcon && (
    <button
      className="flex-shrink-0 w-xl h-xl sm:w-2xl sm:h-2xl pt-px pb-px pl-[6px] pr-2xs flex items-center justify-center bg-transparent border-none cursor-pointer"
      onClick={(e) => {
        e.stopPropagation();
        onToggleExpand?.();
      }}
      aria-label={isExpanded ? "Close" : "Open"}
      type="button"
    >
      <MakulaIcon
        icon={<ChevronDown />}
        className={`w-lg h-lg transition-transform duration-200 ${
          isExpanded ? "" : "-rotate-90"
        }`}
        color="text-tertiary"
      />
    </button>
  );

  return (
    <WorkOrderCard
      title={title}
      ticketId={ticketID}
      companyName={facility}
      assets={assets}
      iconName={ticketType?.icon}
      iconColor={ticketType?.color}
      assignees={assignees}
      daysRemaining={daysRemaining}
      scheduledDate={scheduledDateStr}
      statusLabel={statusItem?.label}
      statusColor={statusItem?.color}
      notificationCount={notificationCount}
      subWorkOrderCount={subWorkOrderCount}
      showAssignees
      showDueDate
      showSchedule
      onClick={handleNavigate}
      isExternal={ticketType?.isInternal === false}
      isSelected={isSelected}
      selectable={isSelectMode}
      anySelected={anySelected}
      onToggleSelect={onToggleSelect}
      leadingComponent={showExpandIcon ? expandButton : undefined}
      trailingComponent={
        <DropdownMenuProvider>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="w-3xl h-3xl flex items-center justify-center bg-transparent border-none cursor-pointer"
              aria-label={messages?.common?.more ?? "More"}
            >
              <MakulaIcon
                icon={<MoreIcon />}
                className="w-xl h-xl"
                color="text-secondary"
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={8}
            className="min-w-[180px]"
          >
            <DropdownMenuItem onSelect={handleNavigate}>
              <EyeOpenIcon className="w-lg h-lg flex-shrink-0 text-secondary" />
              {messages?.common?.view ?? "View"}
            </DropdownMenuItem>
            {showArchive && onArchive && (
              <DropdownMenuItem onSelect={onArchive}>
                <ArchiveWorkOrderIcon className="w-lg h-lg flex-shrink-0 text-secondary" />
                {messages?.tickets?.archiveWorkOrder ?? "Archive"}
              </DropdownMenuItem>
            )}
            {showUnarchive && onUnarchive && (
              <DropdownMenuItem onSelect={onUnarchive}>
                <ArchiveWorkOrderIcon className="w-lg h-lg flex-shrink-0 text-secondary" />
                {messages?.tickets?.unarchiveWorkOrder ?? "Unarchive"}
              </DropdownMenuItem>
            )}
            {showDelete && onDelete && (
              <DropdownMenuItem onSelect={onDelete} className="!text-danger">
                <DeleteIcon className="w-lg h-lg flex-shrink-0" />
                {messages?.tickets?.deleteWorkOrder ?? "Delete"}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenuProvider>
      }
    />
  );
};

export default WorkOrderListCard;
